/**
 * @description 季报高光时刻组件 - 2026版本
 * @date 2025-01-24
 * @author lv.chen
 */
import React from 'react';
import classnames from 'classnames';
import Image from '@user-center/components/Image';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { getGameEntityTypeClassName } from '../../../utils';

import ICON_PAGE from '../../assets/images/icon-page.png';
import ICON_VISIT from '../../assets/images/icon-visit.png';

interface IHighlightMomentProps extends IBaseProps {
  quarterChars?: string;
  report?: IQuarterReportDetails | null;
}

/**
 * 季报高光时刻组件
 */
const HighlightMoment: React.FC<IHighlightMomentProps> = (props) => {
  const { style, className, quarterChars, report } = props;

  const { formatMessage } = useIntlExtend();

  // 获取最常访问游戏数据
  const topVisitedGame = report?.top_visit_game?.[0];
  // 获取最常访问次数数据
  const topVisitedTimes = report?.top_visit_times?.[0];

  // 按 \n 拆分文案，前半段跟日期同行，后半段留在 desc
  const visitedGameText = (formatMessage(
    { id: 'dbu_quarter_report_visited_game_on_date' },
    { gameName: topVisitedGame?.game_name },
  ) as string) || '';
  const [visitedGamePrefix, ...visitedGameRestParts] = visitedGameText.split('\n');
  const visitedGameRest = visitedGameRestParts.join('\n');

  // 按 \n 拆分"访问DataBrain"文案，前半段跟日期同行，后半段留在 desc
  const visitedDbText = (formatMessage(
    { id: 'dbu_quarter_report_visited_databrain_on_date' },
  ) as string) || '';
  const [visitedDbPrefix, ...visitedDbRestParts] = visitedDbText.split('\n');
  const visitedDbRest = visitedDbRestParts.join('\n');

  return (
    <div style={style} className={classnames('hight-moment', className, { '--no-visit': !topVisitedGame })}>
      <div className="quarter-report-title">
        <span>{formatMessage({ id: 'dbu_quarter_report_highlight_moment' })}</span>
      </div>
      <div className="hight-moment-content">
        <div className="hight-moment-list">
          <div className="hight-moment-item">
            <div className="hight-moment-item-icon">
              <Image src={ICON_PAGE} />
            </div>
            <div className="hight-moment-item-text">
              <div className="hight-moment-item-title">
                <span>{formatMessage({ id: 'dbu_quarter_report_most_visited_page' }, { quarter: quarterChars })}</span>
              </div>
              <div className="hight-moment-item-desc">「{report?.top_visit_page}」</div>
            </div>
          </div>
          {!!topVisitedGame && (
            <div className="hight-moment-item">
              <div
                className={classnames(
                  'hight-moment-item-image',
                  getGameEntityTypeClassName(topVisitedGame.entity_type),
                )}
              >
                <Image src={topVisitedGame.cover || ''} />
              </div>
              <div className="hight-moment-item-text">
                <div className="hight-moment-item-title">
                  <span className="hight-moment-item-title-date">{topVisitedGame.date}</span>
                  {visitedGamePrefix && <span className="hight-moment-item-title-suffix">{visitedGamePrefix}</span>}
                </div>
                <div className="hight-moment-item-desc">
                  {visitedGameRest}
                  <span className="hight-moment-item-nums">
                    <span className="--num">{topVisitedGame.times}</span>
                    {formatMessage({ id: 'dbu_quarter_report_times_unit' })}
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className="hight-moment-item">
            <div className="hight-moment-item-icon">
              <Image src={ICON_VISIT} />
            </div>
            <div className="hight-moment-item-text">
              <div className="hight-moment-item-title">
                <span>{topVisitedTimes?.date}</span>
                {visitedDbPrefix && <span className="hight-moment-item-title-suffix">{visitedDbPrefix}</span>}
              </div>
              <div className="hight-moment-item-desc">
                {visitedDbRest}
                <span className="hight-moment-item-nums">
                  <span className="--num">{topVisitedTimes?.times}</span>
                  {formatMessage({ id: 'dbu_quarter_report_times_unit' })}
                </span>
              </div>
            </div>  
          </div>
        </div>
      </div>
    </div>
  );
};

export default HighlightMoment;
